import { Typography, Button } from "@/design-system/Atoms";
import { consultationsColumns } from "./consultationTable.data";
import { TableGrid } from "@/design-system/Molecules/TableGrid";
import { DASHBOARD_TABS } from "@/constants/dashboardTabs";
import { AppEmptyState, AppErrorState } from "@/design-system/Feedbacks";
import ScheduleTableShimmer from "./consultationTableShimmer";
import { useScrollToTopOnMount } from "@/hooks/useScrollToTopOnMount";
import { useEffect, useMemo } from "react";
import { useSelfScheduleService } from "@/rtkQuery/features/hooks/useSelfScheduleService";
import type { Appointment } from "@/rtkQuery/features/models/selfSchedule.model";
import type { AppointmentRow } from "./consultationTable.data";

export const ConsultationsSection = () => {
  useScrollToTopOnMount();
  
  const {
    fetchRecentAppointments,
    recentAppointmentsData,
    recentAppointmentsIsLoading,
    recentAppointmentsIsError,
    recentAppointmentsError,
    recentAppointmentsErrorMessage,
  } = useSelfScheduleService();
 
  useEffect(() => {
    fetchRecentAppointments();
  }, [fetchRecentAppointments]);

  const formatDate = (dateTimeString: string): string => {
    const date = new Date(dateTimeString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatTime = (dateTimeString: string): string => {
    const date = new Date(dateTimeString);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const calculateEndTime = (startDateTime: string, duration: number): string => {
    const startDate = new Date(startDateTime);
    const endDate = new Date(startDate.getTime() + duration * 60000);
    return endDate.toISOString();
  };

  const isBadRequestError = useMemo(() => {
    if (!recentAppointmentsError) return false;
    const error = recentAppointmentsError as any;
    return (
      error?.status === 400 ||
      error?.data?.status === 400 ||
      (error?.originalStatus === 400) ||
      (typeof error === 'object' && 'status' in error && error.status === 400)
    );
  }, [recentAppointmentsError]);

  const isAppointmentExpired = (endDateTime: string): boolean => {
    const endDate = new Date(endDateTime);
    const now = new Date();
    return endDate < now;
  };

  const handleJoinZoom = (joinUrl: string, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation(); 
    }
    if (joinUrl) {
      window.open(joinUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const appointments = useMemo((): AppointmentRow[] => {
    if (!recentAppointmentsData || recentAppointmentsData.length === 0) {
      return [];
    }

    return recentAppointmentsData.map((apt: Appointment) => {
      const endDateTime = calculateEndTime(apt.StartDateTime, apt.Duration);
      return {
        id: apt.AppointmentId.toString(),
        date: formatDate(apt.StartDateTime),
        time: `${formatTime(apt.StartDateTime)} - ${formatTime(endDateTime)} (EST)`,
        status: apt.Status,
        provider: apt.Provider,
        joinUrl: apt.JoinUrl || undefined,
        endDateTime: endDateTime,
      };
    });
  }, [recentAppointmentsData]);
  return (
    <div className="rounded-[20px] bg-[#f6f7f9] p-8" id="v-pills-tabContent">
      <div
        id={DASHBOARD_TABS.CONSULTATIONS}
        role="tabpanel"
        aria-labelledby={DASHBOARD_TABS.CONSULTATIONS}
        className="block opacity-100"
      >
        <Typography
          variant="darkBackgroundHeading"
          as="h3"
          className="flex animate-fade-in-up justify-between"
        >
          Consultations
        </Typography>
        {recentAppointmentsIsLoading ? (
          <ScheduleTableShimmer />
        ) : recentAppointmentsIsError || isBadRequestError ? (
          <div className="w-fit mx-auto px-4">
            <div className="rounded-[20px] min-h-[40vh] lg:min-h-[35vh] flex items-center justify-center">
              <AppErrorState 
                message={recentAppointmentsErrorMessage || "Failed to load appointments."} 
              />
            </div>
          </div>
        ) : !appointments.length || !recentAppointmentsData || recentAppointmentsData.length === 0 ? (
          <div className="w-fit mx-auto px-4">
            <div className="rounded-[20px] min-h-[40vh] lg:min-h-[35vh] flex items-center justify-center">
              <AppEmptyState message="No scheduled appointments found." />
            </div>
          </div>
        ) : (
          <TableGrid 
            columns={[...consultationsColumns]} 
            data={appointments}
            renderActions={(row: AppointmentRow) => {
              if (!row.joinUrl) return null;
              
              const isExpired = row.endDateTime ? isAppointmentExpired(row.endDateTime) : false;
              
              return (
                <Button
                  variant="secondary"
                  size="md"
                  className="whitespace-nowrap"
                  aria-label={isExpired ? "Appointment time has passed" : "Join Meeting"}
                  disabled={isExpired}
                  onClick={(e) => !isExpired && handleJoinZoom(row.joinUrl!, e)}
                >
                  Join Meeting
                </Button>
              );
            }}
          />
        )}
      </div>
    </div>
  );
}; 
export default ConsultationsSection;